import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Upload, FileText, Trash2, CheckCircle, Heart, Baby, Stethoscope, Calendar, Building2, LogOut } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Utility function to convert date to DD/MM/YYYY format
const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return '';
  
  // If already in DD/MM/YYYY format, return as is
  if (dateString.includes('/')) return dateString;
  
  // Handle ISO date format
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Helper function to open S3 files with presigned URLs
const openFileWithPresignedUrl = async (fileUrl) => {
  if (!fileUrl) {
    console.error('No file URL provided');
    return;
  }
  
  console.log('Opening file:', fileUrl);
  
  // Check if it's an S3 URL
  if (fileUrl.includes('s3.') && fileUrl.includes('amazonaws.com')) {
    try {
      // Extract S3 key from URL
      const urlParts = fileUrl.split('.amazonaws.com/');
      if (urlParts.length > 1) {
        const s3Key = urlParts[1].split('?')[0]; // Remove any query params
        console.log('Fetching presigned URL for S3 key:', s3Key);
        // Get presigned URL from backend
        const response = await axios.get(`${API}/api/s3-file/${s3Key}`);
        if (response.data && response.data.url) {
          window.open(response.data.url, '_blank');
          return;
        }
      }
    } catch (error) {
      console.error('Error getting presigned URL:', error);
    }
  }
  
  // For local files or fallback, open directly
  let url;
  if (fileUrl.startsWith('http')) {
    url = fileUrl;
  } else if (fileUrl.startsWith('/api/')) {
    url = `${API}${fileUrl}`;
  } else {
    url = `${API}${fileUrl}`;
  }
  
  console.log('Opening URL:', url);
  window.open(url, '_blank');
};

const ReportsUpload = () => {
  const [loginData, setLoginData] = useState({ baby_id: '', dob: '' });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [baby, setBaby] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [uploadedReports, setUploadedReports] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Restore session on component mount
  React.useEffect(() => {
    const storedBaby = sessionStorage.getItem('reportsUploadBaby');
    const storedSessions = sessionStorage.getItem('reportsUploadSessions');
    
    if (storedBaby && storedSessions) {
      try {
        setBaby(JSON.parse(storedBaby));
        setSessions(JSON.parse(storedSessions));
        setIsLoggedIn(true);
      } catch (error) {
        console.error('Error restoring session:', error);
        sessionStorage.removeItem('reportsUploadBaby');
        sessionStorage.removeItem('reportsUploadSessions');
      }
    }
  }, []);

  // Handle ID input - enforce 8 character uppercase format
  const handleIdInput = (e) => {
    let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''); // Only alphanumeric, uppercase
    
    // Limit to 8 characters
    if (value.length > 8) {
      value = value.substring(0, 8);
    }
    
    setLoginData(prev => ({ ...prev, baby_id: value }));
  };

  // Handle DOB input with DD/MM/YYYY format
  const handleDobInput = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5, 9);
    }
    
    setLoginData(prev => ({ ...prev, dob: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Validate ID is 8 characters
    if (loginData.baby_id.length !== 8) {
      toast.error('ID must be exactly 8 characters');
      return;
    }
    
    // Validate DOB format
    if (loginData.dob.length !== 10) {
      toast.error('Please enter complete date of birth (DD/MM/YYYY)');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/api/baby-login`, null, {
        params: {
          baby_id: loginData.baby_id.trim(),
          dob: loginData.dob
        }
      });
      
      setBaby(response.data.baby);
      
      // Filter sessions: Only show sessions with tests recommended and not older than 72 hours after first report upload
      const filteredSessions = response.data.sessions.filter(session => {
        // Must have tests recommended
        if (!session.tests || session.tests.trim().length === 0) {
          return false;
        }
        
        // Check if any reports uploaded for this session
        // If reports exist, check if 72 hours have passed since first upload
        // This will be checked when loading reports for session
        return true;
      });
      
      setSessions(filteredSessions);
      setIsLoggedIn(true);
      
      // Store in sessionStorage for persistence across page refreshes
      sessionStorage.setItem('reportsUploadBaby', JSON.stringify(response.data.baby));
      sessionStorage.setItem('reportsUploadSessions', JSON.stringify(filteredSessions));
      
      toast.success(`Welcome! Logged in as ${response.data.baby.name}`);
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.status === 404) {
        toast.error('Baby not found with this ID');
      } else if (error.response?.status === 401) {
        toast.error('Invalid date of birth');
      } else {
        toast.error('Login failed. Please try again.');
      }
    }
  };

  const handleSessionSelect = async (sessionId) => {
    const session = sessions.find(s => s.id === sessionId);
    setSelectedSession(session);
    
    // Load existing reports for this session
    try {
      const response = await axios.get(`${API}/api/report-uploads/session/${sessionId}`);
      const reports = response.data;
      
      // Check if 72 hours have passed since first report upload
      if (reports.length > 0) {
        const firstUpload = new Date(reports[0].upload_date);
        const now = new Date();
        const hoursPassed = (now - firstUpload) / (1000 * 60 * 60);
        
        if (hoursPassed > 72) {
          // Remove this session from the list after 72 hours
          setSessions(prev => prev.filter(s => s.id !== sessionId));
          setSelectedSession(null);
          toast.info('This session is no longer available for report uploads (72 hours passed)');
          return;
        }
      }
      
      setUploadedReports(reports);
    } catch (error) {
      console.error('Error loading reports:', error);
      setUploadedReports([]);
    }
  };

  const handleFileUpload = async (testName, files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    let successCount = 0;
    let failCount = 0;
    
    try {
      // Upload all files sequentially
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        try {
          // Upload file first
          const formData = new FormData();
          formData.append('file', file);
          
          const uploadResponse = await axios.post(`${API}/api/upload-image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          // Create report upload record
          const reportData = {
            baby_id: baby.id,
            session_id: selectedSession.id,
            test_name: testName,
            report_url: uploadResponse.data.url,
            uploaded_by: 'parent',
            notes: file.name // Store filename as note
          };
          
          const reportResponse = await axios.post(`${API}/api/report-uploads`, reportData);
          
          setUploadedReports(prev => [...prev, reportResponse.data]);
          successCount++;
        } catch (fileError) {
          console.error(`Error uploading ${file.name}:`, fileError);
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} report(s) uploaded for ${testName}`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} report(s) failed to upload`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload reports');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    try {
      await axios.delete(`${API}/api/report-uploads/${reportId}`);
      setUploadedReports(prev => prev.filter(r => r.id !== reportId));
      toast.success('Report deleted');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete report');
    }
  };

  const getTestsFromSession = (session) => {
    if (!session || !session.tests) return [];
    return session.tests.split(',').map(t => t.trim()).filter(t => t.length > 0);
  };

  const getTestReports = (testName) => {
    return uploadedReports.filter(r => r.test_name === testName);
  };

  const isTestUploaded = (testName) => {
    return uploadedReports.some(r => r.test_name === testName);
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4">
              <Upload className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl flex items-center justify-center gap-2">
              <FileText className="w-6 h-6 text-blue-600" />
              Upload Medical Reports
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Login with ID and Date of Birth
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="baby_id">ID</Label>
                <Input
                  id="baby_id"
                  placeholder="Enter 8-character ID (e.g., A1B2C3D4)"
                  value={loginData.baby_id}
                  onChange={handleIdInput}
                  maxLength={8}
                  required
                  className="uppercase"
                />
                {loginData.baby_id && loginData.baby_id.length < 8 && (
                  <p className="text-xs text-amber-600 mt-1">ID must be 8 characters</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="dob">Date of Birth (DD/MM/YYYY)</Label>
                <Input
                  id="dob"
                  placeholder="DD/MM/YYYY"
                  value={loginData.dob}
                  onChange={handleDobInput}
                  maxLength={10}
                  required
                />
              </div>
              
              <Button type="submit" className="w-full">
                Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <Card className="mb-6 border-2 border-blue-200">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
                  <Baby className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle>
                    {baby.name}
                  </CardTitle>
                  <p className="text-sm text-gray-600 mt-1">ID: {baby.short_id || baby.id.substring(0, 8).toUpperCase()}</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={() => {
                  setIsLoggedIn(false);
                  setBaby(null);
                  setSessions([]);
                  setSelectedSession(null);
                  setUploadedReports([]);
                  // Clear sessionStorage
                  sessionStorage.removeItem('reportsUploadBaby');
                  sessionStorage.removeItem('reportsUploadSessions');
                  toast.success('Logged out successfully');
                }}
                className="flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Session Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Select Medical Session
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sessions.length === 0 ? (
              <p className="text-gray-600">No medical sessions found for this baby.</p>
            ) : (
              <div className="space-y-4">
                <Label>Session Date</Label>
                <Select onValueChange={handleSessionSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a session..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sessions.map(session => {
                      // Format date to DD/MM/YYYY
                      const formattedDate = formatDateToDDMMYYYY(session.session_date);
                      // Use formatted_doctor_name if available (includes specialization), otherwise use doctor_name
                      const doctorName = session.formatted_doctor_name || 
                        (session.doctor_name.startsWith('Dr') ? session.doctor_name : `Dr. ${session.doctor_name}`);
                      // Get hospital name
                      const hospitalName = session.hospital_name || session.updated_by_hospital || '';
                      
                      return (
                        <SelectItem key={session.id} value={session.id}>
                          {formattedDate} - {doctorName}
                          {hospitalName && ` - ${hospitalName}`}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tests Upload Section */}
        {selectedSession && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Upload Test Reports
              </CardTitle>
              <div className="text-sm text-gray-600 mt-2 space-y-1">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Session: {formatDateToDDMMYYYY(selectedSession.session_date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Stethoscope className="w-4 h-4" />
                  <span>{selectedSession.doctor_name.startsWith('Dr') ? selectedSession.doctor_name : `Dr. ${selectedSession.doctor_name}`}</span>
                </div>
                {(selectedSession.hospital_name || selectedSession.updated_by_hospital) && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    <span>{selectedSession.hospital_name || selectedSession.updated_by_hospital}</span>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {getTestsFromSession(selectedSession).length === 0 ? (
                <p className="text-gray-600">No tests recommended in this session.</p>
              ) : (
                <div className="space-y-4">
                  {getTestsFromSession(selectedSession).map((test, index) => {
                    const testReports = getTestReports(test);
                    const hasReports = testReports.length > 0;
                    
                    return (
                      <div key={index} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <FileText className="w-5 h-5 text-gray-600" />
                            <span className="font-medium">{test}</span>
                            {hasReports && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                {testReports.length} file(s)
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Show uploaded reports */}
                        {hasReports && (
                          <div className="space-y-2 mb-3">
                            {testReports.map((report) => (
                              <div key={report.id} className="bg-green-50 border border-green-200 rounded p-3 text-sm flex items-center justify-between">
                                <div>
                                  <p className="text-green-800 font-medium">
                                    {report.notes || 'Report'} 
                                  </p>
                                  <button 
                                    onClick={() => openFileWithPresignedUrl(report.report_url)}
                                    className="text-blue-600 hover:underline text-xs cursor-pointer bg-transparent border-none p-0"
                                  >
                                    View File
                                  </button>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteReport(report.id)}
                                  disabled={(() => {
                                    if (!report.upload_date) return false;
                                    const uploadDate = new Date(report.upload_date);
                                    const now = new Date();
                                    const hoursDiff = (now - uploadDate) / (1000 * 60 * 60);
                                    return hoursDiff > 24;
                                  })()}
                                  title={(() => {
                                    if (!report.upload_date) return 'Delete report';
                                    const uploadDate = new Date(report.upload_date);
                                    const now = new Date();
                                    const hoursDiff = (now - uploadDate) / (1000 * 60 * 60);
                                    return hoursDiff > 24 ? 'Cannot delete reports older than 24 hours' : 'Delete report';
                                  })()}
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Upload more files */}
                        <div className="mt-2">
                          <Input
                            type="file"
                            accept="image/*,.pdf"
                            multiple
                            onChange={(e) => {
                              const files = Array.from(e.target.files);
                              if (files.length > 0) {
                                handleFileUpload(test, files);
                                e.target.value = ''; // Reset input
                              }
                            }}
                            disabled={uploading}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {hasReports ? 'Upload additional files' : 'Select one or more files to upload'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ReportsUpload;
